import '@formatjs/intl-relativetimeformat/locale-data/ar'
import '@formatjs/intl-relativetimeformat/locale-data/en'
import '@formatjs/intl-relativetimeformat/locale-data/fr'
import '@formatjs/intl-relativetimeformat/locale-data/tr'
import '@formatjs/intl-relativetimeformat/locale-data/ur'
import '@formatjs/intl-relativetimeformat/polyfill'
import {importWithRetry} from 'common/helpers/chunkLoad'
import {isSelectiveAiDomainHost} from 'common/helpers/selectiveAiDomain'
import {FC, useEffect, useState} from 'react'
import {IntlConfig, IntlProvider, useIntl} from 'react-intl'

import {WithChildren} from '../helpers'
import {useLang} from './Metronici18n'
import {setActiveMessages} from './messageRegistry'

// Matches what IntlProvider expects for its messages prop, which is keyed by
// TranslationKey through the global augmentation in react-intl.d.ts.
type Messages = NonNullable<IntlConfig['messages']>

// Each locale is a separate lazy chunk — only the active one is downloaded. Retried in
// place on a dropped request: every screen waits on this chunk.
const loaders: Record<string, () => Promise<{default: Record<string, string>}>> = {
  ar: () => importWithRetry(() => import('./messages/ar')),
  en: () => importWithRetry(() => import('./messages/en')),
  fr: () => importWithRetry(() => import('./messages/fr')),
  tr: () => importWithRetry(() => import('./messages/tr')),
  ur: () => importWithRetry(() => import('./messages/ur')),
}

/**
 * Tenant wording variants. A key `X.SELECTIVE_AI` holds the Da1TimeTable (selective-AI host)
 * wording for key `X`; on those hosts it replaces `X`, elsewhere it is ignored. Keeps call
 * sites tenant-agnostic and lets the i18n sync/check tooling treat variants as ordinary keys.
 */
const SELECTIVE_AI_SUFFIX = '.SELECTIVE_AI'

export const applyTenantOverrides = (
  messages: Record<string, string>,
  isSelectiveAi: boolean = isSelectiveAiDomainHost()
): Record<string, string> => {
  if (!isSelectiveAi) return messages
  const out = {...messages}
  for (const [key, value] of Object.entries(messages)) {
    if (key.endsWith(SELECTIVE_AI_SUFFIX)) {
      out[key.slice(0, -SELECTIVE_AI_SUFFIX.length)] = value
    }
  }
  return out
}

const IntlInitializer: FC = () => {
  const intl = useIntl()
  window.intl = intl
  return null
}

const I18nProvider: FC<WithChildren> = ({children}) => {
  const locale = useLang()
  const [messages, setMessages] = useState<Messages | null>(null)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const mod = await (loaders[locale] || loaders.en)()
        if (cancelled) return
        const resolved = applyTenantOverrides(mod.default)
        setActiveMessages(resolved)
        setMessages(resolved as Messages)
      } catch (e) {
        if (locale !== 'en') {
          // Locale chunk failed (bad network / stale deploy) — fall back to en.
          try {
            const mod = await loaders.en()
            if (cancelled) return
            const resolved = applyTenantOverrides(mod.default)
            setActiveMessages(resolved)
            setMessages(resolved as Messages)
            return
          } catch {
            // fall through to the error state below
          }
        }
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)))
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [locale])

  // Surface async chunk-load failures to the ChunkErrorBoundary above us.
  if (error) throw error

  // Changing language reloads the page (see Metronici18n.setLanguage), so
  // this only gates the very first paint while the locale chunk loads.
  if (!messages) return null

  return (
    <IntlProvider locale={locale} messages={messages}>
      {children}
      <IntlInitializer />
    </IntlProvider>
  )
}

export {I18nProvider}
